import { useState, useRef, useCallback, useEffect } from 'react'
import { getStateCheckboxOptions } from '../../jurisdiction-sources/state-jurisdictions-mappings'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'
import { MAX_SELECTED_STATES } from '../../constants'
import { StateSelectionProps } from '../../types'
import { WARNING_DISPLAY_MS } from '../../constants'

type StateContentProps = {
  conversationId: string
  stateSelectionProps: StateSelectionProps
}

export default function StateContent(props: StateContentProps) {
  const { stateSelectionProps } = props
  const { selectedStates, handleStateToggle } = stateSelectionProps

  // Transient warning shown when user attempts to exceed MAX_SELECTED_STATES.
  // Distinct from the StatesFilterOptions warning banner (which warns about missing state selection).
  const [showMaxWarning, setShowMaxWarning] = useState(false)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    }
  }, [])

  const flashMaxWarning = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    setShowMaxWarning(true)
    warningTimerRef.current = setTimeout(() => setShowMaxWarning(false), WARNING_DISPLAY_MS)
  }, [])

  const states = getStateCheckboxOptions()
  const deviceType = useDeviceType()
  const isMobile = deviceType === DEVICE_TYPE.MOBILE
  const isAtMax = selectedStates.length >= MAX_SELECTED_STATES

  const handleRowClick = (stateId: string) => {
    const isChecked = selectedStates.includes(stateId)
    if (!isChecked && isAtMax) {
      flashMaxWarning()
      return
    }
    setShowMaxWarning(false)
    handleStateToggle(stateId)
  }

  return (
    <>
      <div className="pt-2 md:p-6">
        <div
          className={`flex flex-wrap items-center gap-x-2 px-6 md:px-2 h-8 mb-1.5 rounded-md text-sm transition-all ${
            showMaxWarning ? 'bg-yellow-50 duration-150' : 'bg-transparent duration-500'
          }`}
        >
          <span>
            <span className="font-semibold text-brand-neutral-950">Select up to {MAX_SELECTED_STATES} states</span>
            <span className="text-brand-neutral-600">
              {' '}
              ({selectedStates.length} of {MAX_SELECTED_STATES})
            </span>
          </span>
          <span className={`text-yellow-800 text-xs font-medium transition-opacity ${showMaxWarning ? 'opacity-100 duration-150' : 'opacity-0 duration-500'}`}>
            Maximum {MAX_SELECTED_STATES} states
          </span>
        </div>
        <fieldset className="columns-1 md:columns-2 lg:columns-3 md:p-2 mt-0 gap-x-2">
          <legend className="sr-only">Select up to {MAX_SELECTED_STATES} states for case law research</legend>
          {states.map((state) => {
            const isChecked = selectedStates.includes(state.id)
            const isDisabled = !isChecked && isAtMax
            return (
              <label
                key={state.id}
                htmlFor={state.id}
                className={`h-[52px] md:h-auto md:mb-3 flex items-center px-6 md:px-0 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${
                  isMobile ? 'border-b border-brand-neutral-200 last:border-b-0' : ''
                }`}
              >
                <input
                  id={state.id}
                  type="checkbox"
                  value={state.id}
                  checked={isChecked}
                  disabled={isDisabled}
                  aria-disabled={isDisabled}
                  tabIndex={isDisabled ? -1 : undefined}
                  onChange={() => handleRowClick(state.id)}
                  className={`h-4 w-4 rounded border-brand-neutral-300 text-brand-500 focus:ring-brand-500 mr-2 ${
                    isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  aria-label={isDisabled ? `${state.label} — maximum states selected` : `Select ${state.label} for case law research`}
                />
                <span className={`text-sm font-normal tracking-[-0.15px] ${isDisabled ? 'text-brand-neutral-400' : 'text-brand-neutral-950'}`}>
                  {state.label}
                </span>
              </label>
            )
          })}
        </fieldset>
      </div>
    </>
  )
}
